import { Path } from "@kform/core";
import * as React from "react";

import { FormContext, FormContextValue } from "../contexts/FormContext";
import { useFormContext } from "../hooks/useFormContext";
import { useResolvedPath } from "../hooks/useResolvedPath";

/**
 * Properties of the current path component.
 */
export interface CurrentPathProps {
  /**
   * Path to set as the new current path for all children of this component.
   *
   * This path will be resolved against the current path.
   * @default Path.CURRENT
   */
  path?: Path | string;
  children?: React.ReactNode;
}

/**
 * Component used to specify a new current path for its children.
 *
 * This component must be rendered within a form context. The provided path will
 * be resolved against the current path in context.
 */
export function CurrentPath({
  path = Path.CURRENT,
  children,
}: CurrentPathProps) {
  const formContext = useFormContext();
  const resolvedPath = useResolvedPath(path);

  const newFormContext = React.useMemo<FormContextValue>(
    () => ({
      formManager: formContext.formManager,
      currentPath: resolvedPath,
      controller: formContext.controller,
    }),
    [formContext.controller, formContext.formManager, resolvedPath],
  );

  return (
    <FormContext.Provider value={newFormContext}>
      {children}
    </FormContext.Provider>
  );
}
